<script setup lang="ts">
  import type { StashColorNames, StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import { computed, useAttrs, useCssModule } from 'vue';

  import { getColorScheme } from '../../utils/colorScheme';

  defineOptions({
    name: 'll-avatar',
  });

  export interface AvatarProps {
    /**
     * The name of the user will be parsed and have its initial displayed in case the src prop is empty
     */
    name?: string;

    /**
     * The color for the chip that determines both the text and bg color. Needs to
     * be one of the brand colors in our design system (not a shade).
     */
    colorScheme?: StashColorNames;

    /**
     * The shade of the provided color to use for the background color. This effects the inferred
     * text color unless a specific text color is provided. Currently only `light` and `main`
     * shades are supported. `dark` may be added in the future.
     */
    shade?: 'light' | 'main';

    /**
     * The image that will be displayed within the avatar
     */
    src?: string;

    /**
     * The alternative text to be displayed in case the image is unavailable
     */
    alt?: string;

    /**
     * The background color of the chip. Needs to be one of our design system colors.
     */
    bgColor?: StashColorNamesWithShades;

    /**
     * The color of the chip text. Needs to be one of our design system colors.
     */
    textColor?: StashColorNamesWithShades | 'white' | 'black';
  }

  const props = withDefaults(defineProps<AvatarProps>(), {
    src: undefined,
    name: '',
    alt: undefined,
    colorScheme: 'ice',
    shade: 'light',
    bgColor: undefined,
    textColor: undefined,
  });

  const attrs = useAttrs();
  const classes = useCssModule();

  const computedColors = computed(() => {
    return getColorScheme({
      shade: props.shade,
      color: props.colorScheme,
    });
  });

  const initials = computed(() => {
    const [firstName, lastName] = String(props.name).toUpperCase().split(' ');
    const initials = lastName ? firstName.charAt(0) + lastName.charAt(0) : firstName.charAt(0);

    return initials;
  });

  const isClickable = computed(() => {
    return !!attrs.onClick;
  });
</script>

<template>
  <img
    v-if="props.src"
    class="stash-avatar stash-avatar--image inline-block rounded-full"
    :class="[classes.root, { [classes.clickable]: isClickable }]"
    :alt="props.alt"
    :src="props.src"
    :tabindex="isClickable ? 0 : -1"
    data-test="stash-avatar"
    v-bind="attrs"
  />
  <div
    v-else
    class="stash-avatar stash-avatar--initials inline-block cursor-default rounded-full text-center font-semibold"
    :class="[
      classes.root,
      `text-${props.textColor || computedColors.computedTextColor}`,
      `bg-${props.bgColor || computedColors.computedBgColor}`,
      { [classes.clickable]: isClickable },
    ]"
    :tabindex="isClickable ? 0 : -1"
    data-test="stash-avatar"
    v-bind="attrs"
  >
    {{ initials }}
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    font-size: var(--text-sm);
    height: --spacing(9);
    line-height: --spacing(9);
    /* Opt out of Tailwind preflight's `img { max-width: 100% }` so the
   * explicit width below isn't clamped to 0 when an inline-block parent
   * is content-sized (circular sizing dependency). */
    max-width: none;
    width: --spacing(9);
  }

  .clickable:hover,
  .clickable:focus,
  .clickable:active {
    outline: 1px solid var(--color-blue-500);
    outline-offset: -1px;
  }

  .clickable:focus,
  .clickable:active {
    box-shadow: 0 0 0 4px rgb(0 114 240 / 15%);
  }
</style>
